import { useUser } from "@clerk/nextjs";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { useTRPC } from "@/frontend/lib/trpc";

import { useDebounce } from "./utils";

// QUERIES
export function useGithubTokenQuery() {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.github.getGithubTokenStatus.queryOptions(),
    enabled: !!user,
  });
}

export function useRepoSearchQuery(query: string) {
  const { user } = useUser();
  const trpc = useTRPC();
  const debounced = useDebounce(query, 300);
  return useQuery({
    ...trpc.github.searchRepos.queryOptions({ query: debounced }),
    enabled: debounced.length > 1 && !!user,
  });
}

export function useRepoBranchesQuery(repoFullName: string | null) {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.github.listRepoBranches.queryOptions({
      repoFullName: repoFullName!,
    }),
    enabled: Boolean(repoFullName) && !!user,
  });
}

export function useJamGithubExportsQuery(jamId: string | null) {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.github.listJamGithubExports.queryOptions({ jamId: jamId! }),
    enabled: Boolean(jamId) && !!user,
  });
}

export function useGithubExportStatusQuery() {
  const { user } = useUser();
  const trpc = useTRPC();
  return useQuery({
    ...trpc.github.getGithubExportStatus.queryOptions(),
    staleTime: 60 * 1000,
    enabled: !!user,
  });
}

// MUTATIONS
export function useDisconnectGithub() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.github.disconnectGithub.mutationOptions(),
    onSettled: async () => {
      await queryClient.invalidateQueries(
        trpc.github.getGithubTokenStatus.queryFilter(),
      );
      await queryClient.invalidateQueries(
        trpc.github.getGithubExportStatus.queryFilter(),
      );
    },
  });
}

export function useExportToGithub() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  return useMutation({
    ...trpc.github.exportToGithub.mutationOptions(),
    onSettled: async (_, __, variables) => {
      await queryClient.invalidateQueries(
        trpc.github.listJamGithubExports.queryFilter({
          jamId: variables.jamId,
        }),
      );
    },
  });
}
